/**
 * StepWelcome — first wizard step.
 *
 * Two choices: start from scratch, or "I already have a CV to paste".
 * The second option will enable the Import step in Phase C. For now (Phase B),
 * both paths lead to the Basics step, but we still remember the choice so
 * the import step slots in naturally later.
 */

import { Sparkles, ClipboardPaste } from 'lucide-react';
import { useI18n } from '../../../contexts/I18nContext';

interface StepWelcomeProps {
  setShowImportStep: (v: boolean) => void;
  goNext: () => void;
}

export function StepWelcome({ setShowImportStep, goNext }: StepWelcomeProps) {
  const { t } = useI18n();
  const handleFromScratch = () => {
    setShowImportStep(false);
    goNext();
  };

  const handlePasteExisting = () => {
    setShowImportStep(true);
    goNext();
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
          Créons ton CV ensemble
        </h1>
        <p className="text-base text-white/60 max-w-xl mx-auto">
          En quelques minutes, avec l'aide de l'IA, on va structurer ton parcours
          en un CV clair et optimisé pour les ATS.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={handleFromScratch}
          className="group relative flex flex-col items-start gap-3 p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-blue-500/50 transition-all text-left"
        >
          <div className="p-2.5 rounded-xl bg-blue-600/20 text-blue-400 group-hover:bg-blue-600/30 transition-colors">
            <Sparkles className="size-5" />
          </div>
          <div className="space-y-1">
            <div className="text-lg font-bold">Je pars de zéro</div>
            <div className="text-sm text-white/60">
              Je vais décrire mon parcours et l'IA va le structurer en bullets
              ATS à chaque étape.
            </div>
          </div>
          <div className="mt-2 text-xs font-semibold text-blue-400 group-hover:text-blue-300">
            Commencer →
          </div>
        </button>

        <button
          type="button"
          onClick={handlePasteExisting}
          className="group relative flex flex-col items-start gap-3 p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-purple-500/50 transition-all text-left"
        >
          <div className="p-2.5 rounded-xl bg-purple-600/20 text-purple-400 group-hover:bg-purple-600/30 transition-colors">
            <ClipboardPaste className="size-5" />
          </div>
          <div className="space-y-1">
            <div className="text-lg font-bold">
              J'ai déjà un CV à importer
            </div>
            <div className="text-sm text-white/60">
              Je colle mon CV actuel, l'IA le lit et remplit les champs —
              je vérifie juste à la fin.
            </div>
          </div>
          <div className="mt-2 text-xs font-semibold text-purple-400 group-hover:text-purple-300">
            Importer →
          </div>
        </button>
      </div>

      <p className="text-xs text-center text-white/40">
        {t('wizard.autoSave')}
      </p>
    </div>
  );
}
